import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useMutation, UseMutationResult, useQueryClient } from "@tanstack/react-query";

import { refreshTrashEntity } from "../../lib/trash-entity-invalidation";

import { restoreEntitiesMutationDocument, RestoreEntitiesResponse } from "./trash.gql";

interface RestoreEntitiesInput {
  entityType: string;
  idList: string[];
}
interface RestoreEntitiesResult {
  restoredCount: number;
  entityType: string;
}
interface RestoreEntitiesHookResult {
  restoreEntities: UseMutationResult<RestoreEntitiesResult, Error, RestoreEntitiesInput>["mutateAsync"];
  loading: boolean;
  error: Error | null;
}
export const useRestoreEntities = (workspaceId: string): RestoreEntitiesHookResult => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (input: { entityType: string; idList: string[] }) => {
      const client = createOperationClient("RestoreEntities");
      const data = await client.request<RestoreEntitiesResponse>(restoreEntitiesMutationDocument, { input });
      return { restoredCount: data.restoreEntities, entityType: input.entityType };
    },
    onSuccess: async ({ entityType }: RestoreEntitiesResult, { idList }: RestoreEntitiesInput): Promise<void> => {
      await Promise.all([
        refreshTrashEntity({ queryClient, entityType, scope: { workspaceId, idList } }),
        queryClient.invalidateQueries({ queryKey: queryKeys.trash.workspaceLists(workspaceId) }),
      ]);
    },
  });

  return {
    restoreEntities: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
};
